<template>
    <nav class="fixed-top bg-dark shadow-sm border-bottom border-body px-2 py-4">
        <div class="container">
            <div class="d-flex align-items-center justify-content-evenly" role="search">
                <div class="w-50">
                    <div ref="container" class="input-group float-end">
                        <input v-model="searchText" :maxlength="14" placeholder="Email" class="form-control me-2"
                            :class="{ 'rounded-bottom-off': showSearchResults }"
                            style="border-bottom-right-radius: 0.375rem" type="text" aria-label="Email"
                            @input="handleChange" @keydown="onKeyDown" @keydown.down.prevent="onAccountSearchKeyDown"
                            @keydown.up.prevent="onAccountSearchKeyUp" @keydown.enter.prevent="onAccountSelect"
                            @keydown.escape.prevent="onAccountSearchEnd" />

                        <span v-if="recordCount.length > 0" class="record-count">{{ recordCount }}</span>

                        <div v-if="showSearchResults" class="dropdown-list rounded-bottom">
                            <div v-for="account, i in accountResults" :key="i" @click="onAccountClick(i)"
                                :class="i === selectedItemIndex ? 'dropdown-item selected' : 'dropdown-item'">
                                {{ account.email }}
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="showSelectedAccount" class="d-flex float-start w-50">
                    <div>
                        <span class="badge rounded-pill text-bg-light d-flex align-items-center ms-4 me-2 float-end">
                            {{ selectedAccount.email }}
                            <button type="button" class="btn-close btn-sm ms-2" aria-label="Close"
                                @click="clearSelectedAccount">
                            </button>
                        </span>
                    </div>
                </div>
                <div v-else class="w-50">&nbsp;</div>
            </div>
        </div>
    </nav>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { app } from "@/guiide/ts/app";
import { AccountSearchResponse } from '@/guiide/ts/response/accountSearchResponse';
import { AccountSearchResponseItem } from '@/guiide/ts/response/accountSearchResponseItem';
import { useStore } from "vuex";

const minCharsForSearch = 3;
const allowedKeys = ['ArrowLeft', 'ArrowRight', 'ArrowDown', 'ArrowUp', 'Control', 'Shift', 'Delete', 'Backspace', 'Enter', 'Escape', '@', '_', '.', '-', '+'];
const container = ref(null);
const searchText = ref('');
const accountResults = ref<Array<AccountSearchResponseItem>>(new Array<AccountSearchResponseItem>());
const recordCount = ref(' ');
const noSelectedItem = -1;
const selectedItemIndex = ref(noSelectedItem);
const selectedAccount = ref<AccountSearchResponseItem | null>(null);

const store = useStore();

let timeout: ReturnType<typeof setTimeout>;

const delayedSearch = async () => {
    if (searchText.value.length < minCharsForSearch) {
        clearAccountSearchResults();
        return;
    }

    recordCount.value = "...";

    const searchResult: AccountSearchResponse = await app.findAccounts(searchText.value);

    accountResults.value = searchResult.items;
    selectedItemIndex.value = accountResults.value.length > 0 ? 0 : noSelectedItem;
    recordCount.value = searchResult.recordCount.toString();
};

const handleChange = () => {
    if (searchText.value.length >= minCharsForSearch) {
        if (timeout) {
            clearTimeout(timeout);
        }
        timeout = setTimeout(delayedSearch, 500);
    } else {
        clearAccountSearchResults();
    }
};

const showSearchResults = computed<boolean>(() => searchText.value.length >= minCharsForSearch && accountResults.value.length > 0);
const showSelectedAccount = computed<boolean>(() => selectedAccount.value != null);

function clearAccountSearchResults() {
    accountResults.value = new Array<AccountSearchResponseItem>();
    recordCount.value = "";
}

function onKeyDown(event: KeyboardEvent) {
    const allowed = allowedKeys.includes(event.key) || /^[a-zA-Z0-9]$/.test(event.key);
    if (!allowed) {
        event.preventDefault();
        return;
    }

    if (event.key == '@' && searchText.value.includes("@")) {
        event.preventDefault();
    }
}

function onAccountSearchKeyDown() {
    const next = selectedItemIndex.value + 1;
    selectedItemIndex.value = next >= accountResults.value.length ? 0 : next;
}

function onAccountSearchKeyUp() {
    const next = selectedItemIndex.value - 1;
    selectedItemIndex.value = next < 0 ? accountResults.value.length - 1 : next;
}

async function onAccountSelect() {
    if (selectedItemIndex.value == noSelectedItem) {
        return;
    }

    selectedAccount.value = accountResults.value[selectedItemIndex.value];

    store.commit("updateImpersonatingId", selectedAccount.value.id);

    onAccountSearchEnd();

    const withinTransactionOngoing = true;
    const forceViewReload = true;
    await app.getRetiree(withinTransactionOngoing, forceViewReload);
}

function onAccountClick(selectedAccountIndex: number) {
    selectedItemIndex.value = selectedAccountIndex;
    onAccountSelect();
}

function onAccountSearchEnd() {
    clearAccountSearchResults();
    searchText.value = "";
}

async function clearSelectedAccount() {
    selectedAccount.value = null;
    store.commit("updateImpersonatingId", null);
    await app.getRetiree();
}

const handleClickOutside = (event) => {
    if (!container.value.contains(event.target)) {
        onAccountSearchEnd();
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.input-group {
    position: relative;
    width: 80%;
}

.form-control {
    margin: 0 !important;
    height: fit-content;
    padding: 0.375rem 0.5rem !important;
    border-top-right-radius: 0.375rem !important;
    border-bottom-right-radius: 0.375rem !important;
    width: 400px;
}

.rounded-bottom-off {
    border-bottom-left-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
}

.dropdown-list {
    position: absolute;
    top: 100%;
    left: 1px;
    z-index: 10;
    border: 1px solid #ddd;
    background-color: white;
    width: 100%;
}

.dropdown-item {
    padding: 10px;
    cursor: pointer;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    display: block;
}

.dropdown-item:hover {
    background-color: #f0f0f0;
}

.selected {
    background-color: #e0e0e0;
}

.record-count {
    position: absolute;
    top: 50%;
    right: 10px;
    transform: translateY(-50%);
    background-color: rgba(255, 255, 255, 0.7);
    padding: 0 5px;
    border-radius: 5px;
    z-index: 10;
}
</style>